import API from '../../utils/API';
import GraphFuncs from '../../utils/GraphFuncs';
import { useState, useEffect } from 'react';
import Loader from "../../components/Loader";
import moment from 'moment';

// Constants
const UNIT_MAPPINGS = {
    thdv: "THD (V)", othdv: "OTHD (V)", ethdv: "ETHD (V)", thffv: "THFF (V)",
    cfv: "CF (V)", unblv: "Unbl (V)", unbli: "Unbl (I)", thdva: "THD (V)",
    kfi: "KF (I)", ethdi: "ETHD (I)", othdi: "OTHD (I)", thdi: "THD (I)",
    vll: "V", vln: "V", co: "T", "ft³": "ft³", L: "L", pf: "PF"
};

const CIRCUIT_TYPES = {
    KW: 'kw',
    KVA: 'kva',
    KVAR: 'kvar',
    A: 'a',
    VLL: 'vll',
    VLN: 'vln',
    PF: 'pf',
    HARMONICS: 'harmonics',
    GAS: 'gas',
    WATER: 'water',
    CO: 'co',
    TEMPERATURE: "temperature"
};

const ELECTRICITY_TYPES = ['electricity', 'harmonics'];
const TOTALABLE_UNITS = ['kw', 'kva', 'kvar', 'a'];
const DIGITAL_UNITS = ['L', 'ft³', 'co'];

export default function DistributionTable(props) {
    const [commonCircuits, setCommonCircuits] = useState({});
    const [circuitsByType, setCircuitsByType] = useState({});
    const [compareCircuits, setCompareCircuits] = useState({});
    const [loading, setLoading] = useState(false);

    const getUnitLabel = (unit, startDate, endDate) => {
        const detail = props.detail
        const unitMap = {
            kw: ["60", 'day', 'month'].includes(detail) ? { unit: "kWh", total: "kWh" } : { unit: "kW", total: "kWh" },
            kva: ["60", 'day', 'month'].includes(detail) ? { unit: "kVAh", total: "kVAh" } : { unit: "kVA", total: "kVAh" },
            kvar: ["60", 'day', 'month'].includes(detail) ? { unit: "kVArh", total: "kVArh" } : { unit: "kVAr", total: "kVArh" },
            a: { unit: "A", total: "A" },
            vll: { unit: "V", total: "V" },
            vln: { unit: "V", total: "V" },
            pf: { unit: "", total: "" },
            L: { unit: "L", total: "L" },
            "ft³": { unit: "ft³", total: "ft³" },
            temperature: ""
        };

        return unitMap[unit] || UNIT_MAPPINGS[unit] || "";
    };

    const parseNumericValue = (value) => {
        if (typeof value === 'number') return value;
        return parseFloat(String(value).replace(/,/g, '')) || 0;
    };

    const formatNumber = (value, decimals = 2) => {
        return parseFloat(value.toFixed(decimals)).toLocaleString();
    };

    // Data Processing Functions
    const calculateSeriesStats = (series) => {
        if (!series || !Array.isArray(series) || series.length === 0) {
            return { total: 0, high: 0, low: 0, average: 0 };
        }
        const total = series.reduce((sum, value) => sum + value, 0);
        return {
            total,
            high: Math.max(...series),
            low: Math.min(...series),
            average: total / series.length
        };
    };

    const calculateTemperatureStats = (series) => {
        if (!series || !Array.isArray(series) || series.length === 0) {
            return { total: 0, high: 0, low: 0, average: 0 };
        }
        const totalHeating = series.reduce((sum, value) => sum + value.heating_day, 0);
        const totalCooling = series.reduce((sum, value) => sum + value.cooling_day, 0);
        return {
            averageHeating: totalHeating,
            averageCooling: totalCooling,
        }
    };

    const getCircuitLineInfo = async (cancelToken, circuit) => {
        try {
            if (!circuit.series) return null;
            let stats
            let lineInfo
            const unitLabel = getUnitLabel(circuit.unit, circuit.startDate, circuit.endDate);
            if (circuit.type === 'temperature') {
                const series = Object.values(circuit.readings);
                stats = calculateTemperatureStats(series);
                lineInfo = [{
                    name: "Heating Degree Days",
                    runtime: 'N/A',
                    high: 0,
                    low: 0,
                    total: stats.averageHeating,
                    unit: ""
                },
                {
                    name: "Cooling Degree Days",
                    runtime: 'N/A',
                    high: 0,
                    low: 0,
                    total: stats.averageCooling,
                    unit: ""
                }]
            } else {
                const series = Object.values(circuit.series);
                stats = calculateSeriesStats(series);
                lineInfo = {
                    name: circuit.channel_name,
                    runtime: circuit.runtime || 'N/A',
                    total: '-',
                    high: `${formatNumber(stats.high)} ${unitLabel.unit}`,
                    low: `${formatNumber(stats.low)} ${unitLabel.unit}`,
                    average: `${formatNumber(stats.average)} ${unitLabel.unit}`,
                    unit: unitLabel.unit
                }
            }


            // Calculate total for specific units
            if (TOTALABLE_UNITS.includes(circuit.unit)) {
                const sum = await API.getSum(
                    cancelToken,
                    circuit.startDate,
                    circuit.endDate,
                    circuit.id,
                    circuit.unit.toLowerCase()
                );
                lineInfo.total = `${formatNumber(sum.data)} ${unitLabel.total}`;
            }
            if (DIGITAL_UNITS.includes(circuit.unit)) {
                lineInfo.total = `${formatNumber(stats.total)} ${unitLabel.total}`;
                lineInfo.unit = circuit.unit;
            }

            // Calculate price for water and gas
            if (circuit.type === 'water') {
                const price = await API.getPriceW(cancelToken, circuit.startDate, circuit.endDate, circuit.id);
                lineInfo.price = formatNumber(price.data.total);
            } else if (circuit.type === 'gas') {
                const price = await API.getPriceG(cancelToken, circuit.startDate, circuit.endDate, circuit.id);
                lineInfo.price = formatNumber(price.data.total);
            }
            return lineInfo;
        } catch (error) {
            if (error.message === 'cancelling') return null;
            console.error('Error in getCircuitLineInfo:', error);
            return { price: '0', total: '0', high: '0', low: '0', average: '0' };
        }
    };

    const getCommonCircuitValues = async (cancelToken, circuit) => {
        try {
            const priceResponse = await API.getPriceE(
                cancelToken,
                circuit.startDate,
                circuit.endDate,
                circuit.id
            );
            circuit.price = priceResponse.data.total.toFixed(2);
            circuit.runtime = "N/A";
            if (!circuit.virtual) {
                const running = await API.getCircuitRuntime(
                    cancelToken,
                    circuit.startDate,
                    circuit.endDate,
                    circuit.id
                );
                const activeTime = Object.values(running.data.series)
                    .reduce((acc, val) => acc + val, 0);
                circuit.runtime = `${activeTime.toFixed(2)} h`;
            }

            circuit.breaker = '-';
            if (circuit.breaker_amps) {
                const peak = await API.getElectricity(
                    cancelToken,
                    circuit.startDate,
                    circuit.endDate,
                    circuit.id,
                    15,
                    'a'
                );
                const peakValue = Math.max(...Object.values(peak.data.series));
                const capacity = ((peakValue.toFixed(2) / 3) / circuit.breaker_amps) * 100;
                circuit.breaker = `${capacity.toFixed(2)}%`;
            }
            return circuit
        } catch (error) {
            if (error.message !== 'cancelling') {
                console.error('Error in getCommonCircuitValues:', error);
            }
        }
    };

    const getCompareValues = async (cancelToken, circuit) => {
        try {

            circuit.runtime = "N/A";
            if (!circuit.virtual) {
                const running = await API.getCircuitRuntime(
                    cancelToken,
                    circuit.startDate,
                    circuit.endDate,
                    circuit.id
                );
                const activeTime = Object.values(running.data.series)
                    .reduce((acc, val) => acc + val, 0);
                circuit.runtime = `${activeTime.toFixed(2)} h`;
            }

            return circuit
        } catch (error) {
            if (error.message !== 'cancelling') {
                console.error('Error in getCommonCircuitValues:', error);
            }
        }
    };

    const categorizeCircuit = (circuit) => {
        if (circuit.compare) return 'compare';
        if (circuit.unit === CIRCUIT_TYPES.CO) return CIRCUIT_TYPES.CO;
        if (circuit.type === CIRCUIT_TYPES.GAS) return CIRCUIT_TYPES.GAS;
        if (circuit.type === CIRCUIT_TYPES.WATER) return CIRCUIT_TYPES.WATER;
        if (circuit.type === CIRCUIT_TYPES.HARMONICS) return CIRCUIT_TYPES.HARMONICS;
        if (circuit.type === CIRCUIT_TYPES.TEMPERATURE) return CIRCUIT_TYPES.TEMPERATURE;
        return circuit.unit;
    };

    // Main data fetching effect
    useEffect(() => {
        const cancelToken = API.cancelToken();
        setLoading(true);

        const processCircuits = async () => {
            const categorized = {};
            const compare = {};
            let tempCommonCircuits = {}

            for (const circuitKey in props.circuits) {
                const circuit = props.circuits[circuitKey];
                if (circuit.type === 'temperature' && (!props.compare || !['day', 'month'].includes(props.detail))) continue

                if (!circuit?.channel_name || circuit.channel_name.includes('Cap Bank')) {
                    continue;
                }
                const category = categorizeCircuit(circuit);

                if (category === 'compare') {
                    await getCompareValues(cancelToken, circuit);
                    const lineInfo = await getCircuitLineInfo(cancelToken, circuit);
                    if (lineInfo) {
                        if (!compare[circuit.channel_name]) {
                            compare[circuit.channel_name] = {};
                        }
                        const type = circuit.type === 'gas' ? 'gas' :
                            circuit.type === 'water' ? 'water' :
                                circuit.type === 'temperature' ? 'temperature' :
                                    circuit.unit;
                        compare[circuit.channel_name][type] = lineInfo;
                    }
                    continue;
                }

                // Handle electricity and harmonics common values
                if (ELECTRICITY_TYPES.includes(circuit.type)) {
                    const needsUpdate = !tempCommonCircuits[circuit.channel_name] ||
                        circuit.startDate !== tempCommonCircuits[circuit.channel_name].startDate ||
                        circuit.endDate !== tempCommonCircuits[circuit.channel_name].endDate;

                    if (needsUpdate) {
                        const commonInfo = await getCommonCircuitValues(cancelToken, circuit);
                        if (commonInfo) {
                            tempCommonCircuits[circuit.channel_name] = commonInfo
                        }
                    }
                }
                
                const lineInfo = await getCircuitLineInfo(cancelToken, circuit);
                if (lineInfo) {
                    categorized[category] ??= [];
                    categorized[category].push(...(Array.isArray(lineInfo) ? lineInfo : [lineInfo]));
                }
            }
            setCommonCircuits(tempCommonCircuits);
            setCircuitsByType(categorized);
            setCompareCircuits(compare);
            setLoading(false);
        };

        processCircuits();

        return () => API.cancel(cancelToken);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.circuits]);

    // Calculation Functions
    const sumCircuitValues = (circuits, attribute) => {
        return circuits.reduce((sum, circuit) => {
            if (!circuit) return sum;
            return sum + parseNumericValue(circuit[attribute]);
        }, 0);
    };

    const getCompareSum = (circuits, circuitType) => {
        return circuits.reduce((sum, circuit) => {
            if (!circuit || !compareCircuits[circuit.name]?.[circuitType]) return sum;
            return sum + parseNumericValue(compareCircuits[circuit.name][circuitType].total);
        }, 0);
    };

    const calculateCompareDiff = (circuits, circuitType) => {
        const compareSum = getCompareSum(circuits, circuitType);
        const currentSum = sumCircuitValues(circuits, 'total');

        if (compareSum === 0) return 0;
        return ((compareSum - currentSum) / compareSum) * -100;
    };

    const calculateWeightedAverage = (circuits) => {
        const totalAverage = sumCircuitValues(circuits, 'average');
        if (totalAverage === 0) return 0;

        return circuits.reduce((sum, circuit) => {
            if (!circuit) return sum;
            const avg = parseNumericValue(circuit.average);
            return sum + (avg * avg / totalAverage);
        }, 0);
    };

    const sumCommonValues = (circuits, attribute) => {
        return circuits.reduce((sum, circuit) => {
            if (!circuit || !commonCircuits[circuit.name]) return sum;
            return sum + parseNumericValue(commonCircuits[circuit.name][attribute]);
        }, 0);
    };

    const sumCompare = (circuits, attribute) => {
        return circuits.reduce((sum, circuit) => {
            if (
                !circuit ||
                !circuit.name ||
                !compareCircuits ||
                !compareCircuits[circuit.name]
            ) {
                return sum;
            }
            const firstTypeKey = Object.keys(compareCircuits[circuit.name])[0];
            if (!firstTypeKey) return sum;
            if (!circuit || !compareCircuits[circuit.name]?.[firstTypeKey]) return sum
            return sum + parseNumericValue(compareCircuits[circuit.name]?.[firstTypeKey][attribute]);
        }, 0);
    };

    const convertDigitalUnits = (value, unit, targetType) => {
        const conversions = {
            gas: {
                'ft³': value / 35.3147,
                'L': value / 1000
            },
            water: {
                'ft³': value * 28.3168,
                'L': value
            }
        };
        return conversions[targetType]?.[unit] || 0;
    };

    const sumDigitalCircuits = (circuits, type, attribute) => {
        return circuits.reduce((sum, circuit) => {
            if (!circuit) return sum;
            const value = parseNumericValue(circuit[attribute]);
            return sum + convertDigitalUnits(value, circuit.unit, type);
        }, 0);
    };

    const sumCompareDigital = (circuits, type) => {
        return circuits.reduce((sum, circuit) => {
            if (!circuit || !compareCircuits[circuit.name]?.[type]) return sum;
            const value = parseNumericValue(compareCircuits[circuit.name][type].total);
            return sum + convertDigitalUnits(value, circuit.unit, type);
        }, 0);
    };

    const calculateDigitalCompareDiff = (circuits, type) => {
        const compareSum = sumCompareDigital(circuits, type);
        const currentSum = sumDigitalCircuits(circuits, type, 'total');
        if (compareSum === 0) return 0;
        return ((compareSum - currentSum) / compareSum) * -100;
    };

    const sortByTotal = (a, b) => {
        return parseNumericValue(b.total) - parseNumericValue(a.total);
    };

    // Rendering Components
    const TableHeader = () => (
        <div className='flex flex-nowrap w-content justify-center items-center rounded bg-[#007b4e] text-white p-4 text-sm min-w-[700px]'>
            <div className={`flex flex-nowrap w-content justify-between items-center ${props.compare ? 'w-full' : 'w-1/2'} font-medium`}>
                <p className='w-1/4 text-center'>Circuits</p>
                {props.compare ? (
                    <>
                        <p className='w-1/4 text-center'>Selected Dates</p>
                        <p className='w-1/4 text-center'>Comparative Dates</p>
                        <p className='w-1/4 text-center'>Difference</p>
                    </>
                ) : (
                    <>
                        <p className='w-1/4 text-center'>Total</p>
                        <p className='w-1/4 text-center'>Highest</p>
                        <p className='w-1/4 text-center'>Lowest</p>
                    </>
                )}
            </div>
            {!props.compare && (
                <div className='flex flex-nowrap w-content justify-between items-center w-1/2 font-medium'>
                    <p className='w-1/4 text-center'>Average</p>
                    <p className='w-1/4 text-center'>Breaker Capacity</p>
                    <p className='w-1/4 text-center'>Run time Hour</p>
                    <p className='w-1/4 text-center'>Cost</p>
                </div>
            )}
        </div>
    );

    const CircuitRow = ({ type, circuit, showCommon = true }) => {
        const compareData = compareCircuits?.[circuit.name]?.[type];
        const commonData = commonCircuits?.[circuit.name];
        const breakerValue = commonData?.breaker ? parseInt(commonData.breaker.split(".")[0]) : 0;

        return (
            <div className='flex flex-nowrap w-content justify-center items-center p-4 text-sm min-w-[700px]'>
                <div className={`flex flex-nowrap w-content justify-between items-center ${props.compare ? 'w-full' : 'w-1/2'} font-medium`}>
                    <p className='w-1/4 text-center font-medium text-[#000000]'>{circuit.name}</p>
                    <p className='w-1/4 text-center text-[#332D41]'>{circuit.total} {props.compare && commonData?.runtime && `(${commonData?.runtime})`}</p>
                    {props.compare && compareData ? (
                        <>
                            <p className='w-1/4 text-center'>{compareData.total}  {props.compare && commonData?.runtime && `(${compareData?.runtime})`}</p>
                            <p className='w-1/4 text-center'>
                                {((parseNumericValue(compareData.total) - parseNumericValue(circuit.total)) /
                                    parseNumericValue(compareData.total) * -100).toFixed(2)}%
                            </p>
                        </>
                    ) :
                        !props.compare ? (
                            <>
                                <p className='w-1/4 text-center text-[#332D41]'>{circuit.high}</p>
                                <p className='w-1/4 text-center text-[#332D41]'>{circuit.low}</p>
                            </>
                        ) :
                            null}
                </div>
                {!props.compare && (
                    <div className='flex flex-nowrap w-content justify-between items-center w-1/2 font-medium'>
                        <p className='w-1/4 text-center text-[#332D41]'>{circuit.average}</p>
                        <p className={`w-1/4 text-center ${breakerValue > 80 ? 'text-[red]' : 'text-[#332D41]'}`}>
                            {(showCommon && commonData?.breaker) || '-'}
                        </p>
                        <p className='w-1/4 text-center text-[#332D41]'>{(showCommon && commonData?.runtime) || '-'}</p>
                        <p className='w-1/4 text-center text-[#332D41]'>
                            {showCommon ? ('$' + (commonData?.price ? formatNumber(parseFloat(commonData.price)) : (circuit.price ? circuit.price : '0'))) : " - "}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const TotalRow = ({ circuits, unit, label, showDigital = false, digitalType = null }) => {
        const unitLabel = getUnitLabel(unit, props.startEnd[0], props.startEnd[1]);
        const includeRuntime = !['water', 'gas', 'temperature'].includes(unit)
        const getTotalValue = () => {
            if (showDigital) {
                return `${formatNumber(sumDigitalCircuits(circuits, digitalType, 'total'))} ${digitalType === 'gas' ? 'm³' : 'L'}`;
            }
            if (unit === 'temperature') {
                let circuit = circuits.find(c => c.name === label)
                return `${formatNumber(circuit.total)}`;
            }
            return `${formatNumber(sumCircuitValues(circuits, 'total'))} ${unitLabel.total}`;
        };

        const getCompareValue = () => {
            if (showDigital) {
                return `${formatNumber(sumCompareDigital(circuits, digitalType))} ${digitalType === 'gas' ? 'm³' : 'L'}`;
            }
            if (unit === 'temperature') {
                let circuit = compareCircuits.Temperature.temperature.find(c => c.name === label)
                return `${formatNumber(circuit.total)}`;
            }
            return `${formatNumber(getCompareSum(circuits, unit))} ${unitLabel.unit}`;
        };

        const getDiffValue = () => {
            if (showDigital) {
                return `${formatNumber(calculateDigitalCompareDiff(circuits, digitalType))} %`;
            }
            if (unit === 'temperature') {
                let circuit = circuits.find(c => c.name === label)
                let circuitCompare = compareCircuits.Temperature.temperature.find(c => c.name === label)
                return `${formatNumber(((circuitCompare.total - circuit.total) / circuitCompare.total) * -100)}%`;
            }
            return `${formatNumber(calculateCompareDiff(circuits, unit))} %`;
        };

        return (
            <div className='flex flex-nowrap w-content justify-center items-center p-4 bg-[#EDEDED] rounded text-sm min-w-[700px]'>
                <div className={`flex flex-nowrap w-content justify-between items-center ${props.compare ? 'w-full' : 'w-1/2'} font-medium`}>
                    <p className='w-1/4 text-center font-medium text-[#000000]'>{label}</p>
                    <p className='w-1/4 text-center text-[#332D41]'>{['co', 'harmonics', 'pf', 'vll', 'vln'].includes(unit) ? " - " : getTotalValue()} {props.compare && includeRuntime && `(${formatNumber(sumCommonValues(circuits, 'runtime'))} h)`}</p>
                    {props.compare ? (
                        <>
                            <p className='w-1/4 text-center'>{getCompareValue()} {includeRuntime && `(${formatNumber(sumCompare(circuits, 'runtime'))}h)`}</p>
                            <p className='w-1/4 text-center'>{getDiffValue()}</p>
                        </>
                    ) : (
                        <>
                            <p className='w-1/4 text-center text-[#332D41]'>
                                {['co', 'harmonics', 'pf', 'vll', 'vln'].includes(unit) ? " - " :
                                    (showDigital ?
                                        `${formatNumber(sumDigitalCircuits(circuits, digitalType, 'high'))} ${digitalType === 'gas' ? 'm³' : 'L'}` :
                                        `${formatNumber(sumCircuitValues(circuits, 'high'))} ${unitLabel.unit}`)
                                }
                            </p>
                            <p className='w-1/4 text-center text-[#332D41]'>
                                {['co', 'harmonics', 'pf', 'vll', 'vln'].includes(unit) ? " - " :
                                    (showDigital ?
                                        `${formatNumber(sumDigitalCircuits(circuits, digitalType, 'low'))} ${digitalType === 'gas' ? 'm³' : 'L'}` :
                                        `${formatNumber(sumCircuitValues(circuits, 'low'))} ${unitLabel.unit}`)
                                }
                            </p>
                        </>
                    )}
                </div>
                {!props.compare && (
                    <div className='flex flex-nowrap w-content justify-between items-center w-1/2 font-medium'>
                        <p className='w-1/4 text-center text-[#332D41]'>
                            {['co', 'harmonics'].includes(unit) ? " - " :
                                (showDigital ?
                                    `${formatNumber(sumDigitalCircuits(circuits, digitalType, 'average'))} ${digitalType === 'gas' ? 'm³' : 'L'}` :
                                    `${formatNumber(calculateWeightedAverage(circuits))} ${unitLabel.unit}`)
                            }
                        </p>
                        <p className='w-1/4 text-center text-[#332D41]'>-</p>
                        <p className='w-1/4 text-center text-[#332D41]'>
                            {showDigital || ['co'].includes(unit) ? '-' : `${formatNumber(sumCommonValues(circuits, 'runtime'))} h`}
                        </p>
                        <p className='w-1/4 text-center text-[#332D41]'>
                            {['co'].includes(unit) ? " - " :
                                '$' +
                                (showDigital ?
                                    formatNumber(circuits.reduce((sum, c) => sum + parseNumericValue(c?.price), 0)) :
                                    formatNumber(sumCommonValues(circuits, 'price')))
                            }
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderCircuitSection = (type, label, showCommon = true, isDigital = false) => {
        const circuits = circuitsByType[type];
        if (!circuits || circuits.length === 0) return null;
        const sortedCircuits = [...circuits].sort(sortByTotal);

        return (
            <>
                {type !== 'temperature' &&
                    sortedCircuits.map((circuit, idx) => (
                        <CircuitRow key={`${type}-${idx}`} type={type} circuit={circuit} showCommon={showCommon} />
                    ))}
                <TotalRow
                    circuits={sortedCircuits}
                    unit={type}
                    label={label}
                    showDigital={isDigital}
                    digitalType={type}
                />
            </>
        );
    };

    return (
        <div className='card tablet:p-8 mobile:p-2 mt-8 rounded-lg relative w-full'>
            <div className='flex flex-nowrap justify-between items-center'>
                <h2 className="tablet:text-4xl mobile:text-xl font-bold text-[#332D41] mb-2">
                    Historical Usage
                </h2>
                {loading && <Loader />}
            </div>

            <p className='mb-4 font-light text-sm'>
                Date Range: <span className='font-normal'>
                    {moment(props.startEnd[0]).format("MMM DD, YYYY")} - {moment(props.startEnd[1]).format("MMM DD, YYYY")}
                    ({GraphFuncs.getDifferenceInDays(props.startEnd[0], props.startEnd[1])} days)
                </span>
            </p>

            {props.compare?.length === 2 && (
                <p className='mb-4 font-light text-sm'>
                    Comparative Range: <span className='font-normal'>
                        {moment(props.compare[0]).format("MMM DD, YYYY")} - {moment(props.compare[1]).format("MMM DD, YYYY")}
                        ({GraphFuncs.getDifferenceInDays(props.compare[0], props.compare[1])} days)
                    </span>
                </p>
            )}

            <div className='w-full overflow-x-auto'>
                <TableHeader />
                {renderCircuitSection(CIRCUIT_TYPES.KW, 'Total')}
                {renderCircuitSection(CIRCUIT_TYPES.KVA, 'Total')}
                {renderCircuitSection(CIRCUIT_TYPES.KVAR, 'Total')}
                {renderCircuitSection(CIRCUIT_TYPES.A, 'Total')}
                {!props.compare && renderCircuitSection(CIRCUIT_TYPES.VLL, 'Total', true)}
                {!props.compare && renderCircuitSection(CIRCUIT_TYPES.VLN, 'Total', true)}
                {!props.compare && renderCircuitSection(CIRCUIT_TYPES.PF, 'Total', true)}
                {!props.compare && renderCircuitSection(CIRCUIT_TYPES.HARMONICS, 'Total', true)}
                {renderCircuitSection(CIRCUIT_TYPES.GAS, 'Total (Natural Gas)', true, true)}
                {renderCircuitSection(CIRCUIT_TYPES.WATER, 'Total (Water)', true, true)}
                {renderCircuitSection(CIRCUIT_TYPES.CO, 'Total (CO₂)', false)}
                {props.compare && ['day', 'month'].includes(props.detail) && renderCircuitSection(CIRCUIT_TYPES.TEMPERATURE, 'Heating Degree Days')}
                {props.compare && ['day', 'month'].includes(props.detail) && renderCircuitSection(CIRCUIT_TYPES.TEMPERATURE, 'Cooling Degree Days')}
            </div>
        </div>
    );
}